/**
 * Admin Panel - User Management
 * SuperAdmin only page
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, authFetch } from '../AuthContext';
import './AdminPanel.css';

function AdminPanel() {
    const { user, isSuperAdmin, API_URL, logout, isAuthenticated, loading: authLoading } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        role: 'Staff'
    });

    const fetchUsers = useCallback(async () => {
        try {
            const response = await authFetch('/api/auth/users');
            const data = await response.json();
            if (response.ok) {
                setUsers(data.users || []);
            } else if (response.status === 403) {
                // Don't show error for forbidden - user just doesn't have permission
                setError('You do not have permission to manage users.');
            } else {
                setError(data.error || 'Failed to fetch users');
            }
        } catch (err) {
            // Check if it's a session error (already handled by authFetch)
            if (err.message.includes('Session expired')) {
                logout();
            } else {
                setError('Failed to fetch users. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    }, [logout]);

    useEffect(() => {
        // Only fetch if authenticated AND is SuperAdmin
        if (isAuthenticated && isSuperAdmin()) {
            fetchUsers().catch((err) => {
                // Session error - trigger logout
                if (err.message.includes('Session expired')) {
                    logout();
                } else {
                    console.error('Failed to fetch users:', err.message);
                }
            });
        } else {
            // Not authenticated or not SuperAdmin, stop loading
            setLoading(false);
        }
    }, [isAuthenticated, isSuperAdmin, fetchUsers, logout]);

    const handleCreateUser = async (e) => {
        e.preventDefault();
        setError('');
        setSubmitting(true);

        try {
            const response = await authFetch('/api/auth/users', {
                method: 'POST',
                body: JSON.stringify(formData),
            });

            const data = await response.json();

            if (response.ok) {
                setShowModal(false);
                setFormData({ name: '', email: '', role: 'Staff' });
                fetchUsers();
            } else {
                setError(data.error || 'Failed to create user');
            }
        } catch (err) {
            if (err.message.includes('Session expired')) {
                logout();
            } else {
                setError('Failed to create user');
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm('Are you sure you want to delete this user?')) {
            return;
        }

        try {
            const response = await authFetch(`/api/auth/users/${userId}`, {
                method: 'DELETE',
            });

            if (response.ok) {
                fetchUsers();
            } else {
                const data = await response.json();
                setError(data.error || 'Failed to delete user');
            }
        } catch (err) {
            if (err.message.includes('Session expired')) {
                logout();
            } else {
                setError('Failed to delete user');
            }
        }
    };

    const handleToggleUserStatus = async (userId, currentStatus) => {
        try {
            const response = await authFetch(`/api/auth/users/${userId}`, {
                method: 'PUT',
                body: JSON.stringify({ is_active: !currentStatus }),
            });

            if (response.ok) {
                fetchUsers();
            } else {
                const data = await response.json();
                setError(data.error || 'Failed to update user');
            }
        } catch (err) {
            if (err.message.includes('Session expired')) {
                logout();
            } else {
                setError('Failed to update user');
            }
        }
    };

    // Check if user is SuperAdmin
    const userIsSuperAdmin = !authLoading && isSuperAdmin();

    return (
        <div className="admin-panel">
            {authLoading ? (
                <div style={{ padding: '40px', textAlign: 'center' }}>
                    <div>Loading...</div>
                </div>
            ) : !userIsSuperAdmin ? (
                <div style={{ padding: '40px', textAlign: 'center' }}>
                    <h2>Access Denied</h2>
                    <p>You don't have permission to view this page.</p>
                </div>
            ) : (
                <>
                    <div className="admin-header">
                        <h1>User Management</h1>
                        <button 
                            className="btn btn-primary"
                            onClick={() => setShowModal(true)}
                        >
                            + Add New User
                        </button>
                    </div>

                    {error && (
                        <div className="alert alert-error" onClick={() => setError('')}>
                            {error} ×
                        </div>
                    )}

                    {loading ? (
                        <div className="loading">Loading users...</div>
                    ) : (
                        <div className="users-table-container">
                            <table className="users-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Role</th>
                                        <th>Status</th>
                                        <th>Last Login</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map(user => (
                                        <tr key={user.id}>
                                            <td>{user.name}</td>
                                            <td>{user.email}</td>
                                            <td>
                                                <span className={`role-badge role-${user.role.toLowerCase()}`}>
                                                    {user.role}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`status-badge status-${user.is_active ? 'active' : 'inactive'}`}>
                                                    {user.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td>
                                                {user.last_login_at 
                                                    ? new Date(user.last_login_at).toLocaleDateString()
                                                    : 'Never'
                                                }
                                            </td>
                                            <td>
                                                <div className="action-buttons">
                                                    <button
                                                        className="btn btn-sm"
                                                        onClick={() => handleToggleUserStatus(user.id, user.is_active)}
                                                    >
                                                        {user.is_active ? 'Deactivate' : 'Activate'}
                                                    </button>
                                                    <button
                                                        className="btn btn-sm btn-danger"
                                                        onClick={() => handleDeleteUser(user.id)}
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Create User Modal */}
                    {showModal && (
                        <div className="modal-overlay" onClick={() => setShowModal(false)}>
                            <div className="modal" onClick={e => e.stopPropagation()}>
                                <h2>Add New User</h2>
                                <form onSubmit={handleCreateUser}>
                                    <div className="form-group">
                                        <label>Name</label>
                                        <input
                                            type="text"
                                            value={formData.name}
                                            onChange={e => setFormData({...formData, name: e.target.value})}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Email</label>
                                        <input
                                            type="email"
                                            value={formData.email}
                                            onChange={e => setFormData({...formData, email: e.target.value})}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Role</label>
                                        <select
                                            value={formData.role}
                                            onChange={e => setFormData({...formData, role: e.target.value})}
                                        >
                                            <option value="Staff">Staff</option>
                                            <option value="SuperAdmin">SuperAdmin</option>
                                            <option value="Viewer">Viewer</option>
                                        </select>
                                    </div>
                                    <div className="modal-actions">
                                        <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                                            Cancel
                                        </button>
                                        <button type="submit" className="btn btn-primary" disabled={submitting}>
                                            {submitting ? 'Creating...' : 'Create User'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

export default AdminPanel;
